import React, { useState, useEffect, useRef } from 'react';
import './ImageGalleryModal.css';

/**
 * Icon - A stable wrapper for Feather icons to prevent React from crashing
 * when Feather replaces the <i> tag with an <svg> tag.
 */
const Icon = ({ name, className = '' }) => {
  const iconRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.feather && iconRef.current) {
      window.feather.replace();
    }
  }, [name]);

  return (
    <span className={`icon-wrapper ${className}`} ref={iconRef}>
      <i data-feather={name}></i>
    </span>
  );
};

/**
 * ImageGalleryModal Component
 * Displays images in a gallery view with navigation arrows and thumbnail strip
 */
export function ImageGalleryModal({ isOpen, onClose, images, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [fitToScreen, setFitToScreen] = useState(true);
  const thumbnailRef = useRef(null);

  // Update current index when initialIndex changes (e.g., when opening modal)
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  // Re-initialize Feather icons when component updates
  useEffect(() => {
    if (typeof window !== 'undefined' && window.feather && isOpen) {
      const timer = setTimeout(() => {
        try {
          window.feather.replace();
        } catch (e) {
          // Ignore errors during replacement
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentIndex]);

  // Scroll thumbnail into view when current index changes
  useEffect(() => {
    if (thumbnailRef.current && isOpen) {
      const thumbnail = thumbnailRef.current.querySelector(`[data-index="${currentIndex}"]`);
      if (thumbnail) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen || !images || images.length === 0) return;

    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrevious) {
        setCurrentIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight' && hasNext) {
        setCurrentIndex(prev => Math.min(images.length - 1, prev + 1));
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images, onClose]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleToggleFit = () => {
    setFitToScreen(!fitToScreen);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="image-gallery-modal-overlay" onClick={handleBackdropClick}>
      <div className="image-gallery-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          className="image-gallery-modal-close"
          onClick={onClose}
          aria-label="Close"
          title="Close (Esc)"
        >
          <Icon name="x" />
        </button>

        {/* Main image container */}
        <div className="image-gallery-main">
          {/* Previous arrow */}
          {hasPrevious && (
            <button
              className="image-gallery-nav image-gallery-nav-left"
              onClick={handlePrevious}
              aria-label="Previous image"
              title="Previous (←)"
            >
              <Icon name="chevron-left" />
            </button>
          )}

          {/* Image display */}
          <div className="image-gallery-image-container">
            <img
              src={currentImage}
              alt={`${currentIndex + 1} of ${images.length}`}
              className={`image-gallery-image ${fitToScreen ? 'fit-to-screen' : 'full-size'}`}
            />
          </div>

          {/* Next arrow */}
          {hasNext && (
            <button
              className="image-gallery-nav image-gallery-nav-right"
              onClick={handleNext}
              aria-label="Next image"
              title="Next (→)"
            >
              <Icon name="chevron-right" />
            </button>
          )}
        </div>

        {/* Controls bar */}
        <div className="image-gallery-controls">
          {/* Image counter */}
          <div className="image-gallery-counter">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Fit to screen toggle */}
          <button
            className="image-gallery-fit-toggle"
            onClick={handleToggleFit}
            title={fitToScreen ? 'Show full size' : 'Fit to screen'}
            aria-label={fitToScreen ? 'Show full size' : 'Fit to screen'}
          >
            <Icon name={fitToScreen ? 'maximize' : 'minimize'} />
          </button>
        </div>

        {/* Thumbnail strip */}
        <div className="image-gallery-thumbnails" ref={thumbnailRef}>
          {images.map((image, index) => (
            <div
              key={index}
              data-index={index}
              className={`image-gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img src={image} alt={`Thumbnail ${index + 1}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

